// footer.component.ts
import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { Router, RouterLink, RouterModule, NavigationEnd } from '@angular/router';
import { ConstantsService } from '../../constants/constants.service';
import { HttpService } from '../../services/http.service';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { QrScannerComponent } from '../qr-scanner/qr-scanner.component';
import { filter } from 'rxjs/operators';


@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    RouterModule,
    RouterLink,
    FormsModule,
    QrScannerComponent,
    CommonModule,
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent {
  activeNavItem: string = 'home';
  showRequestDetailsModal: boolean = false;
  invitationLink: string = '';
  linkCopied = false;
  formSubmitLoader2: boolean = false;
  visitInvitation: any = {
    visitPurpose: { purposeTypeId: null },
    visitLocation: { visitLocationId: null },
  };
  showSuccessModal = false;
  isMobile: boolean = false;
  isMenuOpen: boolean = false;
  openScanner = false;
  isMenu2Open = false;
  isLinkGenerating: boolean = false;


  constructor(
    public constants: ConstantsService,
    private httpService: HttpService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Update active nav item based on current route
    this.updateActiveNavItemFromRoute();

    // Subscribe to route changes to update active tab
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateActiveNavItemFromRoute();
      });
  }
  
  private updateActiveNavItemFromRoute(): void {
    const url = this.router.url;

    if (url.includes('/approval-requests')) {
      this.setActiveNavItem('menu');
    } 
  }

  setActiveNavItem(navItem: string) {
    this.activeNavItem = navItem;
  }

  logout() {
    console.log('logout');
    this.constants.clearSession();
    this.router.navigate(['/splash']);
  }

  toggleSidebar() {
    this.setActiveNavItem('menu');
    const wrapper = document.getElementById('main-wrapper');
    if (wrapper) {
      wrapper.classList.toggle('show-sidebar');
    }
  }

  viewVisitsByStatus(status: string) {
    this.setActiveNavItem('pending');
    this.router.navigate(['visit-list'], {
      queryParams: {
        status: status,
      },
    });
  }

  navigateToSearch() {
    this.setActiveNavItem('search');
    // Add your search navigation logic here
  }

  navigateToAddVisit() {
    this.isMenuOpen = false;
    this.setActiveNavItem('add');
    this.router.navigate(['/create-visit']);
  }

  navigateToHome() {
    this.setActiveNavItem('home');

    // Get current user and role information
    const user = this.constants.getSession();
    if (!user || !user.role) {
      // Fallback to dashboard if no role found
      this.router.navigate(['/dashboard']);
      return;
    }

    const currentRole = user.role.keyRole;

    // Role-based navigation logic (same as header component)
    const routeMap: { [key: string]: string } = {
      ADMIN: '/users',
      HOST: '/dashboard',
      RECEPTION: '/todays-visits',
      SECURITY: '/todays-visits',
    };

    // Navigate based on role, with fallback
    const targetRoute = routeMap[currentRole] || '/dashboard';
    this.router.navigate([targetRoute]);
  }

  // GTH-900: Send visit invite via WhatsApp, Email, or Copy Link | By Priya K, 14.08.2025
  // Add button new UI design
  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    // 👇 If menu is opening and link not already cached, generate it
    if (this.isMenuOpen) {
    this.generateInvitationLink();
    }
  }

  toggleMenuForReception(){
    this.isMenu2Open = !this.isMenu2Open;
  }

  // Close menu on Escape key press
  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
    }
    if (this.isMenu2Open) {
      this.isMenu2Open = false;
    }
  }

  // Prevent scrolling when menu is open
  @HostListener('document:touchmove', ['$event'])
  preventScroll(event: TouchEvent): void {
    if (this.isMenuOpen) {
      event.preventDefault();
    }
    if (this.isMenu2Open) {
      event.preventDefault();
    }
  }

  navigateToCreateVisit(): void {
    this.router.navigate(['/create-visit-by-reception']);
    this.isMenu2Open = false;
  }

  shareVia(method: 'whatsapp' | 'sms' | 'email') {
    const requestPayload = {
    visitPurpose: { purposeTypeId: null },
    visitLocation: { visitLocationId: null }
  };
  this.httpService
    .postRequest(
      this.constants.SERVER_URLS['GENERATE_INVITATION_LINK'],
      requestPayload
    )
    .subscribe(
      (res) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          const generatedLink = res.result.link;
          // Only store the ID when needed for email flow
          if (method === 'email') {
            this.visitInvitation.visitInvitationId = res.result.visitInvitationId;
          }

          const message = `Hi, please complete your visit details here: ${generatedLink} - Visitor Bridge`;

          switch (method) {
            case 'whatsapp':
              // Detect iOS
              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
              
              if (isIOS) {
                // iOS: Use whatsapp:// scheme to open WhatsApp with share sheet
                const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
                window.location.href = whatsappUrl;
              } else {
                // Android: Use wa.me which shows contact list
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }
              break;

            case 'sms':
              // Open SMS app without prefilled number
              const smsUrl = `sms:?&body=${encodeURIComponent(message)}`;
              window.open(smsUrl, '_self'); // _self recommended for mobile compatibility
              break;

            case 'email':
              this.showRequestDetailsModal = true;
              break;
          }
        } else {
          this.toastr.error(res.message);
        }
      },
      () => {
        this.toastr.error('Failed to generate link.',  );
      }
    );
  this.isMenuOpen = false;
}

  copyLink() {
  if (!this.invitationLink) {
    this.toastr.error('No link available. Please try again.');
    return;
  }
  const message = `Hi, please complete your visit details here: ${this.invitationLink} - Visitor Bridge`;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(message)
      .then(() => {
        this.linkCopied = true;
        this.toastr.success('Link copied successfully!', 'Copied');
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
        // CLOSE the menu after successful copy
      this.isMenuOpen = false;
      })
      .catch(() => {
        this.fallbackCopyTextToClipboard(message);
      });
  } else {
    this.fallbackCopyTextToClipboard(message);
  }
}

private fallbackCopyTextToClipboard(text: string) {
  const el = document.createElement('div');
  el.contentEditable = 'true';
  el.innerText = text;
  el.style.position = 'fixed';
  el.style.left = '-999999px';
  el.style.top = '-999999px';
  el.style.opacity = '0';
  document.body.appendChild(el);
  setTimeout(() => {
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.linkCopied = true;
        this.toastr.success('Link copied successfully!', 'Copied');
        // close menu here as well
        this.isMenuOpen = false;

        // Reset copied badge briefly
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } else {
        this.toastr.error('Failed to copy the link.',  );
      }
    } catch (err) {
      this.toastr.error('Failed to copy the link.',  );
    }
    document.body.removeChild(el);
  }, 100);
}


  proceedToRequestDetails() {
    this.isMenuOpen = false;
    this.showRequestDetailsModal = true;
  }

  closeRequestDetailsModal() {
    this.visitInvitation = {
      visitPurpose: { purposeTypeId: null },
      visitLocation: { visitLocationId: null },
    };
    this.showRequestDetailsModal = false;
    this.isMenuOpen = true;
  }

  requestDetails() {
    this.formSubmitLoader2 = true;

    this.httpService
      .postRequest(
        this.constants.SERVER_URLS['GENERATE_INVITATION_LINK'],
        this.visitInvitation
      )
      .subscribe(
        (res) => {
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            const generatedLink = res.result.link;
            this.visitInvitation.visitInvitationId =
              res.result.visitInvitationId;

            const invitationData = {
              visitInvitationId: this.visitInvitation.visitInvitationId,
              receiverEmail: this.visitInvitation.receiverEmail,
              link: generatedLink,
            };

            this.httpService
              .postRequest(
                this.constants.SERVER_URLS['SEND_INVITATION'],
                invitationData
              )
              .subscribe(
                (sendRes: any) => {
                  this.formSubmitLoader2 = false;
                  if (
                    sendRes.status === this.constants.RESPONSE_CODES['SUCCESS']
                  ) {
                    this.closeRequestDetailsModal();
                    this.toastr.success(sendRes.message);
                  } else {
                    this.toastr.error(sendRes.message,  );
                  }
                },
                () => {
                  this.formSubmitLoader2 = false;
                  this.toastr.error('Failed to send invitation.',  );
                }
              );
          } else {
            this.formSubmitLoader2 = false;
            this.toastr.error(res.message);
          }
        },
        () => {
          this.formSubmitLoader2 = false;
          this.toastr.error('Failed to generate link.',  );
        }
      );
  }

  // QR

  handleScan(event: any) {
    const result = event?.detail?.result;
    if (result) {
      console.log('Scanned from header:', result);
    }
    this.openScanner = false;
  }
  private generateInvitationLink() {
  // Prevent duplicate calls if already generating
  if (this.isLinkGenerating) return;

  this.isLinkGenerating = true;

  const requestPayload = {
    visitPurpose: { purposeTypeId: null },
    visitLocation: { visitLocationId: null }
  };

  this.httpService
    .postRequest(
      this.constants.SERVER_URLS['GENERATE_INVITATION_LINK'],
      requestPayload
    )
    .subscribe(
      (res) => {
        this.isLinkGenerating = false;
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.invitationLink = res.result.link;
        } else {
          this.toastr.error(res.message);
        }
      },
      () => {
        this.isLinkGenerating = false;
        this.toastr.error('Failed to generate link.',  );
      }
    );
}

  
}